// Azure Pronunciation Assessment client — REST API, direct audio capture.
//
// History: we tried two SDK paths (npm package, CDN-loaded browser bundle).
// Both hit different bugs — "class extends undefined" on iPad Safari, and
// "silent capture" on Mac browsers. The SDK is famously fragile on WebKit
// and even on Chromium it has its own audio pipeline that races against
// the UI in ways we can't observe. So we drop it entirely.
//
// REST flow (everything browser-native, no third-party JS):
//   press wand     → startRecording()        — getUserMedia + Web Audio capture
//   release wand   → stopAndAssess(refText)  — downsample, encode WAV, POST
//
// Endpoint: https://{region}.stt.speech.microsoft.com/speech/recognition/conversation/cognitiveservices/v1
// Pronunciation assessment opted in via base64-encoded JSON header.
// Docs: https://learn.microsoft.com/azure/ai-services/speech-service/how-to-pronunciation-assessment?pivots=programming-language-rest

export interface AzureConfig {
  key: string;
  region: string;
}

export interface AzurePronunciationResult {
  accuracy: number;
  fluency: number;
  completeness: number;
  pronunciation: number;
  syllables: Array<{
    syllable: string;
    accuracy: number;
    tonePassed: boolean;
    errorType: string;
  }>;
  transcript: string;
  durationMs: number;
  audioMs: number;
}

const TARGET_SAMPLE_RATE = 16000;

// ── Config validation (no audio, no mic) ───────────────────────────────

export type ValidateResult =
  | { ok: true; tokenPreview: string }
  | { ok: false; status: number; statusText: string; body: string; hint: string };

export async function validateConfig(cfg: AzureConfig): Promise<ValidateResult> {
  const url = `https://${cfg.region}.api.cognitive.microsoft.com/sts/v1.0/issueToken`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Ocp-Apim-Subscription-Key': cfg.key, 'Content-Length': '0' },
    });
  } catch (e) {
    return {
      ok: false,
      status: 0,
      statusText: 'network error',
      body: (e as Error).message,
      hint: 'Could not reach Azure at all. Likely the region string is wrong — copy the value from the portal\'s resource Overview page.',
    };
  }
  if (res.ok) {
    const token = await res.text();
    return { ok: true, tokenPreview: token.slice(0, 12) + '…' };
  }
  const body = await res.text().catch(() => '');
  let hint = '';
  if (res.status === 401) hint = 'Key is wrong or rotated. Copy KEY 1 (or KEY 2) from Azure portal → Keys and Endpoint again.';
  else if (res.status === 403) hint = 'Key valid but resource disabled or rate-limited.';
  else if (res.status === 404) hint = 'Region mismatch — your resource is in a different region. Check the portal Overview page.';
  return { ok: false, status: res.status, statusText: res.statusText, body: body.slice(0, 200), hint };
}

// ── Audio capture + pronunciation assessment ───────────────────────────

export class AzureSpeechClient {
  // Surfaced to the caller for diagnostics on failure.
  lastError: string = '';

  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private chunks: Float32Array[] = [];
  private sourceSampleRate = TARGET_SAMPLE_RATE;
  private recording = false;
  private recordStartedAt = 0;

  constructor(private cfg: AzureConfig) {}

  isRecording(): boolean { return this.recording; }

  async startRecording(): Promise<void> {
    this.lastError = '';
    this.chunks = [];
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    this.audioContext = new Ctx();
    this.sourceSampleRate = this.audioContext.sampleRate;
    const source = this.audioContext.createMediaStreamSource(this.mediaStream);
    // ScriptProcessorNode is deprecated but the most portable across iPad Safari
    // and Mac browsers. AudioWorklet requires module URL plumbing that's more
    // friction than worthwhile here.
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = (e) => {
      const data = e.inputBuffer.getChannelData(0);
      // Web Audio reuses the underlying buffer between callbacks — copy.
      this.chunks.push(new Float32Array(data));
    };
    source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);
    this.recording = true;
    this.recordStartedAt = performance.now();
  }

  // Stop the mic, encode the captured audio as a WAV blob, POST to Azure,
  // parse the JSON response. Returns null on network or HTTP error.
  async stopAndAssess(referenceText: string): Promise<AzurePronunciationResult | null> {
    if (!this.recording) {
      this.lastError = 'stopAndAssess called without an active recording';
      return null;
    }
    this.recording = false;
    const audioMs = Math.round(performance.now() - this.recordStartedAt);

    if (this.processor) { try { this.processor.disconnect(); } catch {} this.processor = null; }
    const sampleRate = this.sourceSampleRate;
    if (this.audioContext) { try { await this.audioContext.close(); } catch {} this.audioContext = null; }
    if (this.mediaStream) { this.mediaStream.getTracks().forEach((t) => t.stop()); this.mediaStream = null; }

    const total = this.chunks.reduce((s, c) => s + c.length, 0);
    if (total === 0) {
      this.lastError = 'no audio captured';
      return zeroResult('', audioMs);
    }
    const flat = new Float32Array(total);
    let offset = 0;
    for (const c of this.chunks) { flat.set(c, offset); offset += c.length; }
    this.chunks = [];

    const downsampled = sampleRate === TARGET_SAMPLE_RATE ? flat : downsample(flat, sampleRate, TARGET_SAMPLE_RATE);
    const wav = encodeWAV(downsampled, TARGET_SAMPLE_RATE);

    return this.postToAzure(wav, referenceText, audioMs);
  }

  // Cancel an active recording without sending anything. Used when the
  // user pauses mid-encounter or navigates away.
  cancel(): void {
    if (!this.recording) return;
    this.recording = false;
    if (this.processor) { try { this.processor.disconnect(); } catch {} this.processor = null; }
    if (this.audioContext) { try { this.audioContext.close(); } catch {} this.audioContext = null; }
    if (this.mediaStream) { this.mediaStream.getTracks().forEach((t) => t.stop()); this.mediaStream = null; }
    this.chunks = [];
  }

  private async postToAzure(wav: Blob, referenceText: string, audioMs: number): Promise<AzurePronunciationResult | null> {
    // EnableMiscue=true makes Azure compare actual speech to the reference
    // word-by-word, flagging omissions, insertions, and mispronunciations.
    // The Transcript reflects what was actually heard. With this OFF, Azure
    // assumes you're reading the reference and the transcript echoes it,
    // so the parent can't tell whether their kid said the right word at all.
    const paConfig = btoaUtf8(JSON.stringify({
      ReferenceText: referenceText,
      GradingSystem: 'HundredMark',
      Granularity: 'Phoneme',
      Dimension: 'Comprehensive',
      EnableMiscue: true,
    }));

    const url = `https://${this.cfg.region}.stt.speech.microsoft.com/speech/recognition/conversation/cognitiveservices/v1` +
                `?language=zh-CN&format=detailed`;

    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': this.cfg.key,
          'Content-Type': `audio/wav; codecs=audio/pcm; samplerate=${TARGET_SAMPLE_RATE}`,
          'Pronunciation-Assessment': paConfig,
          'Accept': 'application/json',
        },
        body: wav,
      });
    } catch (e) {
      this.lastError = `network: ${(e as Error).message}`;
      return null;
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      this.lastError = `HTTP ${res.status}: ${text.slice(0, 200)}`;
      return null;
    }

    const data = await res.json().catch((e) => { this.lastError = `JSON parse: ${e.message}`; return null; });
    if (!data) return null;
    return parseResponse(data, audioMs);
  }
}

function zeroResult(transcript: string, audioMs: number): AzurePronunciationResult {
  return { accuracy: 0, fluency: 0, completeness: 0, pronunciation: 0, syllables: [], transcript, durationMs: 0, audioMs };
}

function btoaUtf8(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

// Decimation downsampler. For 48kHz → 16kHz speech, antialiasing isn't
// needed because the source rate is far above the Nyquist for speech bands.
function downsample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) out[i] = input[Math.floor(i * ratio)];
  return out;
}

function encodeWAV(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

function parseResponse(data: any, audioMs: number): AzurePronunciationResult {
  if (data.RecognitionStatus !== 'Success' || !Array.isArray(data.NBest) || !data.NBest[0]) {
    return { accuracy: 0, fluency: 0, completeness: 0, pronunciation: 0, syllables: [], transcript: data.DisplayText || '', durationMs: 0, audioMs };
  }
  const nb = data.NBest[0];
  const pa = nb.PronunciationAssessment ?? {};

  const syllables: AzurePronunciationResult['syllables'] = [];
  for (const word of (nb.Words ?? [])) {
    const wErrType = word.PronunciationAssessment?.ErrorType ?? 'None';
    for (const sy of (word.Syllables ?? [])) {
      const accuracy = sy.PronunciationAssessment?.AccuracyScore ?? 0;
      syllables.push({
        syllable: sy.Syllable ?? '',
        accuracy,
        tonePassed: accuracy >= 70,
        errorType: wErrType,
      });
    }
  }

  return {
    accuracy: pa.AccuracyScore ?? 0,
    fluency: pa.FluencyScore ?? 0,
    completeness: pa.CompletenessScore ?? 0,
    pronunciation: pa.PronScore ?? pa.AccuracyScore ?? 0,
    syllables,
    transcript: nb.Display ?? data.DisplayText ?? '',
    durationMs: Math.round((nb.Duration ?? 0) / 10000),
    audioMs,
  };
}
